import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Mesh, Plane, Program, RenderTarget, Renderer, Texture, Triangle } from 'ogl';

import './FlexCarousel.css';

const PRESETS = {
  liquid: { lensWidth: 0.74, lensHeight: 1.18, tilt: 62, roundness: 1, bend: 0.34, reach: 0.38, curl: 'twist', dispersion: 0.45, liquid: 0, followCursor: false },
  ribbon: { lensWidth: 0.8, lensHeight: 0.8, tilt: 0, roundness: 1, bend: 0.34, reach: 0.34, curl: 'twist', dispersion: 0.4, liquid: 0, followCursor: false },
  vortex: { lensWidth: 0.7, lensHeight: 0.95, tilt: 30, roundness: 1, bend: 0.46, reach: 0.3, curl: 'twist', dispersion: 0.5, liquid: 0, followCursor: false },
  arch: { lensWidth: 0.8, lensHeight: 0.8, tilt: 0, roundness: 1, bend: 0.3, reach: 0.36, curl: 'rise', dispersion: 0.4, liquid: 0, followCursor: false },
};

const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const wrap = (value, size) => ((((value + size / 2) % size) + size) % size) - size / 2;

const cardVertex = `#version 300 es
in vec3 position;
in vec2 uv;
uniform vec4 uRect;
uniform vec2 uResolution;
out vec2 vLocal;
void main() {
  vLocal = vec2(position.x, -position.y) * uRect.zw;
  vec2 point = uRect.xy + vLocal;
  gl_Position = vec4(point.x / uResolution.x * 2.0 - 1.0, 1.0 - point.y / uResolution.y * 2.0, 0.0, 1.0);
}`;

const cardFragment = `#version 300 es
precision highp float;
uniform sampler2D tMap;
uniform vec2 uSize;
uniform vec2 uImage;
uniform float uRadius;
uniform float uAlpha;
uniform float uReady;
uniform float uShift;
uniform float uDpr;
in vec2 vLocal;
out vec4 fragColor;
float roundedBox(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}
void main() {
  float edge = roundedBox(vLocal, uSize * 0.5, min(uRadius, min(uSize.x, uSize.y) * 0.5));
  float mask = clamp(0.5 - edge * uDpr, 0.0, 1.0);
  vec2 local = vLocal / uSize + 0.5;
  float cardAspect = uSize.x / uSize.y;
  float imageAspect = uImage.x / max(uImage.y, 1.0);
  vec2 scale = imageAspect > cardAspect ? vec2(cardAspect / imageAspect, 1.0) : vec2(1.0, imageAspect / cardAspect);
  scale /= 1.08;
  vec2 uv = (vec2(local.x, 1.0 - local.y) - 0.5) * scale + 0.5;
  uv.x += uShift * (1.0 - scale.x) * 0.5;
  vec3 color = mix(vec3(0.13, 0.14, 0.17), texture(tMap, uv).rgb, uReady);
  float alpha = mask * uAlpha;
  fragColor = vec4(color * alpha, alpha);
}`;

const lensVertex = `#version 300 es
in vec2 position;
void main() { gl_Position = vec4(position, 0.0, 1.0); }`;

const lensFragment = `#version 300 es
precision highp float;
uniform sampler2D tScene;
uniform vec2 uResolution;
uniform float uDpr;
uniform vec2 uCenter;
uniform vec2 uHalf;
uniform float uAngle;
uniform float uExponent;
uniform float uInner;
uniform float uOuter;
uniform float uFlow;
uniform float uCurl;
uniform float uDispersion;
uniform float uStrength;
uniform float uSceneAlpha;
out vec4 fragColor;
void main() {
  vec2 frag = gl_FragCoord.xy / uDpr;
  vec2 uv = frag / uResolution;
  vec2 rel = frag - vec2(uCenter.x, uResolution.y - uCenter.y);
  float ca = cos(uAngle);
  float sa = sin(uAngle);
  vec2 local = vec2(ca * rel.x + sa * rel.y, -sa * rel.x + ca * rel.y);
  vec2 k = max(abs(local) / uHalf, vec2(1e-5));
  float nd = pow(pow(k.x, uExponent) + pow(k.y, uExponent), 1.0 / uExponent);
  vec2 grad = pow(k, vec2(uExponent - 1.0)) * sign(local) / uHalf * pow(nd, 1.0 - uExponent);
  float glen = max(length(grad), 1e-6);
  float edge = (nd - 1.0) / glen;
  vec2 outward = grad / glen;
  vec2 normal = vec2(ca * outward.x - sa * outward.y, sa * outward.x + ca * outward.y);
  vec2 along = vec2(-normal.y, normal.x);
  float t = clamp((edge + uInner) / (uInner + uOuter), 0.0, 1.0);
  float ramp = t * t * t * (t * (t * 6.0 - 15.0) + 10.0);
  float slope = 16.0 * t * t * (1.0 - t) * (1.0 - t);
  float reachX = rel.x / (uResolution.x * 0.5);
  float side = smoothstep(0.02, 0.3, abs(reachX)) * (uCurl == 0.0 ? sign(reachX) : uCurl);
  float lift = ramp * side * uFlow * uStrength;
  vec2 swirl = along * along.y * side * slope * uFlow * uStrength * 0.35;
  vec2 shifted = uv + (vec2(0.0, -lift) - swirl) / uResolution;
  vec2 texels = uResolution * uDpr;
  vec2 gx = dFdx(shifted);
  vec2 gy = dFdy(shifted);
  gx *= min(1.0, 3.0 / max(length(gx * texels), 1e-4));
  gy *= min(1.0, 3.0 / max(length(gy * texels), 1e-4));
  vec4 color = textureGrad(tScene, shifted, gx, gy);
  vec2 spread = vec2(0.0, side * slope * uFlow * uStrength) / uResolution * uDispersion;
  float spreadPx = length(spread * texels);
  if (color.a > 0.002 && spreadPx > 0.25) {
    vec3 base = color.rgb / color.a;
    vec3 sumColor = vec3(0.0);
    vec3 sumWeight = vec3(0.0);
    for (int i = 0; i < 8; i++) {
      float s = (float(i) + 0.5) / 8.0;
      vec4 sampleColor = textureGrad(tScene, shifted + spread * (s - 0.5), gx, gy);
      vec3 weight = max(1.0 - abs(vec3(s) - vec3(0.15, 0.5, 0.85)) * 2.6, 0.0) * sampleColor.a;
      sumColor += sampleColor.rgb * (weight / max(sampleColor.a, 0.002));
      sumWeight += weight;
    }
    vec3 split = mix(base, sumColor / max(sumWeight, vec3(1e-4)), clamp(sumWeight * 2.0, 0.0, 1.0));
    color.rgb = mix(color.rgb, clamp(split, 0.0, 1.0) * color.a, smoothstep(0.25, 1.5, spreadPx));
  }
  fragColor = color * uSceneAlpha;
}`;

export default function FlexCarousel({
  items = [], preset = 'liquid', intro = 'rise', cardHeight = 0.5, gap = 12,
  squeeze = 0.2, focusOnClick = true, captions = true, fit = 'natural', radius = 0,
  lensWidth, lensHeight, tilt, roundness, bend, reach, curl, dispersion, liquid,
  followCursor, autoplay = false, interval = 4, captureWheel = true,
  onChange, onSelect, ariaLabel = 'Image carousel', className = '', style,
}) {
  const base = PRESETS[preset] || PRESETS.liquid;
  const settings = useMemo(() => ({ cardHeight, gap, squeeze, fit, radius, intro, focusOnClick, autoplay, interval, captureWheel,
    lensWidth: lensWidth ?? base.lensWidth, lensHeight: lensHeight ?? base.lensHeight,
    tilt: tilt ?? base.tilt, roundness: roundness ?? base.roundness,
    bend: bend ?? base.bend, reach: reach ?? base.reach, curl: curl ?? base.curl,
    dispersion: dispersion ?? base.dispersion, liquid: liquid ?? base.liquid,
    followCursor: followCursor ?? base.followCursor }), [base, cardHeight, gap, squeeze, fit, radius, intro, focusOnClick, autoplay, interval, captureWheel,
    lensWidth, lensHeight, tilt, roundness, bend, reach, curl, dispersion, liquid, followCursor]);
  const rootRef = useRef(null);
  const engineRef = useRef(null);
  const callbacksRef = useRef({ onChange, onSelect });
  const settingsRef = useRef(settings);
  const [active, setActive] = useState(0);
  const [webglReady, setWebglReady] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [focused, setFocused] = useState(false);
  const [fallbackWidth, setFallbackWidth] = useState(1200);
  const pointerRef = useRef(null);
  const lastWheelRef = useRef(0);
  const reducedMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const itemKey = useMemo(() => items.map((item) => item.src).join('|'), [items]);
  const activeItem = items[active] || items[0];

  useEffect(() => { callbacksRef.current = { onChange, onSelect }; settingsRef.current = settings; }, [onChange, onSelect, settings]);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;
    const update = () => setFallbackWidth(root.clientWidth || window.innerWidth || 1200);
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(root);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root || !items.length || typeof WebGL2RenderingContext === 'undefined') return undefined;
    let renderer;
    try {
      renderer = new Renderer({ dpr: Math.min(window.devicePixelRatio || 1, 2), alpha: true, premultipliedAlpha: true, antialias: false, depth: false });
    } catch { return undefined; }
    if (!renderer.isWebgl2) return undefined;
    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    const canvas = gl.canvas;
    canvas.setAttribute('aria-hidden', 'true');
    canvas.className = 'flex-carousel__canvas';
    root.prepend(canvas);

    const cardProgram = new Program(gl, { vertex: cardVertex, fragment: cardFragment, transparent: true, depthTest: false, depthWrite: false, uniforms: {
      tMap: { value: new Texture(gl) }, uRect: { value: [0, 0, 1, 1] }, uResolution: { value: [1, 1] },
      uSize: { value: [1, 1] }, uImage: { value: [1, 1] }, uRadius: { value: 0 },
      uAlpha: { value: 1 }, uReady: { value: 0 }, uShift: { value: 0 }, uDpr: { value: 1 },
    } });
    cardProgram.setBlendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const cardMesh = new Mesh(gl, { geometry: new Plane(gl), program: cardProgram });
    const target = new RenderTarget(gl, { width: 2, height: 2, depth: false, minFilter: gl.LINEAR_MIPMAP_LINEAR, magFilter: gl.LINEAR });
    const lens = { tScene: { value: target.texture }, uResolution: { value: [1, 1] }, uDpr: { value: 1 },
      uCenter: { value: [0, 0] }, uHalf: { value: [1, 1] }, uAngle: { value: 0 }, uExponent: { value: 2 },
      uInner: { value: 60 }, uOuter: { value: 80 }, uFlow: { value: 0 }, uCurl: { value: 0 },
      uDispersion: { value: 0 }, uStrength: { value: 0 }, uSceneAlpha: { value: 0 } };
    const lensMesh = new Mesh(gl, { geometry: new Triangle(gl), program: new Program(gl, {
      vertex: lensVertex, fragment: lensFragment, uniforms: lens, depthTest: false, depthWrite: false,
    }) });

    let alive = true;
    let width = 1;
    let height = 1;
    let pos = 0;
    let goal = 0;
    let velocity = 0;
    let frameId = 0;
    let visible = true;
    let readyAt = performance.now();
    let introAt = null;
    let last = performance.now();
    let hoverX = 0;
    let hoverY = 0;
    let hovered = false;
    let dragging = false;
    let dragStartX = 0;
    let dragStartPos = 0;
    let dragMoved = false;
    let hits = [];
    let activeIndex = -1;
    let focusIndex = -1;
    let focusTarget = 0;
    let focusAmount = 0;

    const slots = items.map((item) => {
      const texture = new Texture(gl, { generateMipmaps: true, minFilter: gl.LINEAR_MIPMAP_LINEAR, magFilter: gl.LINEAR });
      const slot = { texture, aspect: 1.5, image: [1, 1], loaded: false, failed: false };
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.decoding = 'async';
      image.onload = () => {
        if (!alive) return;
        texture.image = image;
        texture.update();
        slot.image = [image.naturalWidth || 1, image.naturalHeight || 1];
        slot.aspect = slot.image[0] / slot.image[1];
        slot.loaded = true;
        schedule();
      };
      image.onerror = () => { slot.failed = true; schedule(); };
      image.src = item.src;
      slot.dispose = () => { image.onload = null; image.onerror = null; gl.deleteTexture(texture.texture); };
      return slot;
    });

    const metrics = () => {
      const s = settingsRef.current;
      const cardH = Math.max(24, s.cardHeight * height);
      const fixed = { portrait: 0.75, square: 1, landscape: 4 / 3 }[s.fit];
      // Preserve the full natural frame on narrow screens, including wide photos.
      const heights = slots.map((slot) => Math.min(cardH, width * 0.86 / (fixed || slot.aspect)));
      const widths = slots.map((slot, index) => (fixed || slot.aspect) * heights[index]);
      const centers = [];
      let cursor = 0;
      widths.forEach((w) => { centers.push(cursor + w / 2); cursor += w + s.gap; });
      return { heights, widths, centers, loop: Math.max(cursor, 1) };
    };
    const nearest = (m, at) => m.centers.reduce((best, center, i) =>
      Math.abs(wrap(center - at, m.loop)) < Math.abs(wrap(m.centers[best] - at, m.loop)) ? i : best, 0);
    const goTo = (index) => {
      const m = metrics();
      const i = ((index % slots.length) + slots.length) % slots.length;
      goal += wrap(m.centers[i] - goal, m.loop);
      schedule();
    };

    const render = (now) => {
      frameId = 0;
      if (!alive || !visible) return;
      const s = settingsRef.current;
      const dt = clamp((now - last) / 1000, 0.001, 0.05);
      last = now;
      const m = metrics();
      if (introAt === null && (slots.every((slot) => slot.loaded || slot.failed) || now - readyAt > 3500)) {
        introAt = now;
        pos = m.centers[0];
        goal = pos;
        setWebglReady(true);
      }
      if (introAt === null) { schedule(); return; }
      const stiffness = 62;
      velocity += (stiffness * (goal - pos) - 2 * Math.sqrt(stiffness) * velocity) * dt;
      if (!dragging) pos += velocity * dt;
      if (Math.abs(goal - pos) < 0.15 && Math.abs(velocity) < 0.5 && !dragging) { pos = goal; velocity = 0; }
      const current = nearest(m, pos);
      if (current !== activeIndex) {
        activeIndex = current;
        setActive(current);
        callbacksRef.current.onChange?.(current, items[current]);
      }
      focusAmount += (focusTarget - focusAmount) * clamp(dt * 9, 0, 1);
      if (Math.abs(focusTarget - focusAmount) < 0.002) focusAmount = focusTarget;
      if (focusAmount === 0 && focusTarget === 0) focusIndex = -1;
      const duration = reducedMotion || s.intro === 'none' ? 0 : s.intro === 'rise' ? 2100 : 1000;
      const introProgress = duration ? clamp((now - introAt) / duration, 0, 1) : 1;
      if (introProgress === 1) setRevealed(true);
      const eased = 1 - Math.pow(1 - introProgress, 3);
      const alpha = s.intro === 'rise' ? clamp(introProgress * 2.3, 0, 1) : introProgress;
      const dpr = renderer.dpr;
      cardProgram.uniforms.uResolution.value = [width, height];
      cardProgram.uniforms.uDpr.value = dpr;
      cardProgram.uniforms.uRadius.value = s.radius;
      hits = [];
      let first = true;
      for (let i = 0; i < slots.length; i += 1) {
        const slot = slots[i];
        for (let cycle = -1; cycle <= 1; cycle += 1) {
          const relative = wrap(m.centers[i] - pos, m.loop) + cycle * m.loop;
          const w = m.widths[i];
          if (Math.abs(relative) > width / 2 + w / 2 + 50) continue;
          const offset = s.intro === 'rise' ? (1 - eased) * height * (0.4 + Math.abs(relative / width) * 0.2) : 0;
          const energy = clamp(Math.abs(velocity) / 2600, 0, 1);
          const isFocused = i === focusIndex && Math.abs(relative) < w;
          const focusScale = Math.min(1.28, width * 0.94 / w, height * 0.94 / m.heights[i]);
          const scale = (1 - clamp(s.squeeze, 0, 1) * energy) * (isFocused ? 1 + focusAmount * (Math.max(1, focusScale) - 1) : 1);
          const x = width / 2 + relative;
          const y = height / 2 + offset;
          const cw = w * scale;
          const ch = m.heights[i] * scale;
          cardProgram.uniforms.tMap.value = slot.texture;
          cardProgram.uniforms.uRect.value = [x, y, cw + 2, ch + 2];
          cardProgram.uniforms.uSize.value = [cw, ch];
          cardProgram.uniforms.uImage.value = slot.image;
          cardProgram.uniforms.uReady.value = slot.loaded ? 1 : 0;
          cardProgram.uniforms.uShift.value = reducedMotion ? 0 : clamp(relative / (width * 0.75), -1, 1);
          cardProgram.uniforms.uAlpha.value = alpha * (isFocused ? 1 : 1 - focusAmount * 0.86);
          renderer.render({ scene: cardMesh, target, clear: first });
          first = false;
          hits.push({ index: i, left: x - cw / 2, right: x + cw / 2, top: y - ch / 2, bottom: y + ch / 2 });
        }
      }
      if (first) { renderer.bindFramebuffer(target); gl.viewport(0, 0, target.width, target.height); gl.clear(gl.COLOR_BUFFER_BIT); }
      renderer.bindFramebuffer();
      target.texture.bind();
      gl.generateMipmap(gl.TEXTURE_2D);
      const halfW = s.lensWidth * width / 2;
      const halfH = s.lensHeight * width / 2;
      const aimX = s.followCursor && hovered ? hoverX : width / 2;
      const aimY = s.followCursor && hovered ? hoverY : height / 2;
      lens.uResolution.value = [width, height];
      lens.uDpr.value = dpr;
      lens.uCenter.value = [aimX, aimY];
      lens.uHalf.value = [Math.max(halfW, 1), Math.max(halfH, 1)];
      lens.uAngle.value = s.tilt * Math.PI / 180;
      lens.uExponent.value = 2 + Math.pow(1 - clamp(s.roundness, 0, 1), 1.5) * 10;
      lens.uInner.value = Math.max(4, s.reach * (halfW + halfH) * 0.5);
      lens.uOuter.value = lens.uInner.value * 1.6;
      lens.uFlow.value = s.bend * (halfW + halfH) * 0.45;
      lens.uCurl.value = s.curl === 'rise' ? 1 : s.curl === 'fall' ? -1 : 0;
      lens.uDispersion.value = s.dispersion * 0.12;
      lens.uStrength.value = reducedMotion ? 0 : eased * (1 - focusAmount);
      lens.uSceneAlpha.value = alpha;
      renderer.render({ scene: lensMesh });
      if (introProgress < 1 || Math.abs(goal - pos) > 0.15 || Math.abs(velocity) > 0.5 ||
        Math.abs(focusTarget - focusAmount) > 0.002 || dragging || (s.autoplay && !reducedMotion)) schedule();
    };

    const schedule = () => { if (!frameId && visible && alive) frameId = requestAnimationFrame(render); };
    const resize = () => {
      width = Math.max(1, root.clientWidth);
      height = Math.max(1, root.clientHeight);
      renderer.dpr = Math.min(window.devicePixelRatio || 1, 2, Math.sqrt(4500000 / (width * height)));
      renderer.setSize(width, height);
      target.setSize(Math.max(2, Math.round(width * renderer.dpr)), Math.max(2, Math.round(height * renderer.dpr)));
      lens.tScene.value = target.texture;
      schedule();
    };
    const resizeObserver = new ResizeObserver(resize);
    resizeObserver.observe(root);
    const intersectionObserver = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; schedule(); });
    intersectionObserver.observe(root);
    engineRef.current = {
      goTo,
      setFocus: (index) => { if (index !== null) focusIndex = index; focusTarget = index === null ? 0 : 1; schedule(); },
      hit: (x, y) => hits.slice().reverse().find((hit) => x >= hit.left && x <= hit.right && y >= hit.top && y <= hit.bottom),
      dragStart: (x) => { dragging = true; dragMoved = false; dragStartX = x; dragStartPos = pos; velocity = 0; },
      dragMove: (x) => { if (!dragging) return; if (Math.abs(x - dragStartX) > 6) dragMoved = true; if (dragMoved && focusTarget) { focusTarget = 0; setFocused(false); } pos = dragStartPos - (x - dragStartX); goal = pos; schedule(); },
      dragEnd: () => { dragging = false; const m = metrics(); goal += wrap(m.centers[nearest(m, pos)] - goal, m.loop); schedule(); return dragMoved; },
    };
    resize();
    schedule();
    return () => {
      alive = false;
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
      slots.forEach((slot) => slot.dispose());
      engineRef.current = null;
      if (canvas.parentNode) canvas.parentNode.removeChild(canvas);
      gl.getExtension('WEBGL_lose_context')?.loseContext();
    };
  }, [itemKey, items, reducedMotion]);

  if (!items.length) return null;
  const select = (index) => {
    const next = ((index % items.length) + items.length) % items.length;
    setFocused(false);
    engineRef.current?.setFocus(null);
    setActive(next);
    callbacksRef.current.onChange?.(next, items[next]);
    engineRef.current?.goTo(next);
  };
  const open = (index) => {
    if (index !== active) { select(index); return; }
    callbacksRef.current.onSelect?.(index, items[index]);
    if (!focusOnClick) return;
    const next = !focused;
    setFocused(next);
    engineRef.current?.setFocus(next ? index : null);
  };
  const onPointerDown = (event) => {
    if (!webglReady || event.button > 0) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    pointerRef.current = { id: event.pointerId, x, y };
    engineRef.current?.dragStart(x);
    try { event.currentTarget.setPointerCapture(event.pointerId); } catch { /* Pointer capture is optional. */ }
  };
  const onPointerMove = (event) => {
    if (!pointerRef.current || pointerRef.current.id !== event.pointerId) return;
    const rect = event.currentTarget.getBoundingClientRect();
    engineRef.current?.dragMove(event.clientX - rect.left);
  };
  const onPointerUp = (event) => {
    const pointer = pointerRef.current;
    if (!pointer || pointer.id !== event.pointerId) return;
    pointerRef.current = null;
    const moved = engineRef.current?.dragEnd();
    if (moved) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const hit = engineRef.current?.hit(event.clientX - rect.left, event.clientY - rect.top);
    if (!hit) return;
    open(hit.index);
  };
  const onWheel = (event) => {
    if (!captureWheel || event.ctrlKey) return;
    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    if (!delta) return;
    event.preventDefault();
    const now = performance.now();
    if (now - lastWheelRef.current < 220) return;
    lastWheelRef.current = now;
    select(active + Math.sign(delta));
  };
  const onKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') { event.preventDefault(); select(active + 1); }
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') { event.preventDefault(); select(active - 1); }
    if (event.key === 'Home') { event.preventDefault(); select(0); }
    if (event.key === 'End') { event.preventDefault(); select(items.length - 1); }
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); open(active); }
    if (event.key === 'Escape' && focused) { event.preventDefault(); setFocused(false); engineRef.current?.setFocus(null); }
  };

  const fallbackCardWidth = fallbackWidth < 768 ? fallbackWidth * 0.78 : clamp(fallbackWidth * 0.5, 300, 700);
  const fallbackOffset = -((active + 1) * (fallbackCardWidth + gap) + fallbackCardWidth / 2);
  const fallbackItems = [
    { item: items[items.length - 1], index: items.length - 1, duplicate: true },
    ...items.map((item, index) => ({ item, index, duplicate: false })),
    { item: items[0], index: 0, duplicate: true },
  ];

  return (
    <div ref={rootRef} className={`flex-carousel ${className}`} data-webgl={webglReady ? '' : undefined}
      data-focused={focused ? '' : undefined}
      role="group" aria-roledescription="carousel" aria-label={ariaLabel} tabIndex={0}
      style={{ ...style, '--flex-carousel-card-width': `${fallbackCardWidth}px`, '--flex-carousel-card-height': `${cardHeight * 100}%` }}
      onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp}
      onPointerCancel={() => { pointerRef.current = null; engineRef.current?.dragEnd(); }}
      onWheel={onWheel} onKeyDown={onKeyDown}>
      <div className="flex-carousel__track" aria-hidden={webglReady ? 'true' : undefined}
        style={{ transform: `translate3d(${fallbackOffset}px, -50%, 0)` }}>
        {fallbackItems.map(({ item, index, duplicate }, position) => (
          <button key={`${item.src}-${position}`} type="button" className={`flex-carousel__card${index === active && !duplicate ? ' is-active' : ''}`}
            tabIndex={webglReady || duplicate ? -1 : 0}
            aria-hidden={duplicate ? 'true' : undefined}
            aria-label={duplicate ? undefined : `Focus ${item.title || item.alt} image`}
            onClick={(event) => { event.stopPropagation(); open(index); }}>
            <img src={item.src} alt={duplicate ? '' : item.alt || ''} loading={index < 2 ? 'eager' : 'lazy'} />
          </button>
        ))}
      </div>
      {captions && activeItem && (revealed || !webglReady) && (
        <div className="flex-carousel__caption" aria-live="polite">
          <span key={active} className="flex-carousel__title">{activeItem.title}</span>
          {activeItem.subtitle && <span className="flex-carousel__subtitle">{activeItem.subtitle}</span>}
          <span className="flex-carousel__count">{String(active + 1).padStart(2, '0')} / {String(items.length).padStart(2, '0')}</span>
        </div>
      )}
      <div className="flex-carousel__live" aria-live="polite" aria-atomic="true">
        {activeItem ? `${activeItem.title}, ${active + 1} of ${items.length}` : ''}
      </div>
    </div>
  );
}
